import { apiService } from '../core/api.service';
import {
  TenantContext,
  TenantMember,
  TenantRole,
  Group,
  GroupMember,
  GroupAccount,
} from 'src/models/Tenant';
import { Account } from 'src/models/Account';

let tenantContext: TenantContext | undefined = undefined;

export namespace TenantService {

  // ── Cache ──────────────────────────────────────────────

  export function clearCache() {
    tenantContext = undefined;
  }

  // ── Tenant Context ─────────────────────────────────────

  export async function getTenantContext(): Promise<TenantContext> {
    if (tenantContext === undefined) {
      tenantContext = await apiService.get<TenantContext>('/core/get-tenant-context');
    }
    return tenantContext;
  }

  // ── Accounts (tenant-scoped) ───────────────────────────

  export async function getAccounts(): Promise<Account[]> {
    const response = await apiService.get<any[]>('/core/get-accounts-v2');
    if (!Array.isArray(response)) {
      return [];
    }
    return response.map(acc => {
      const account = new Account();
      account.id = acc.accountId || acc.id;
      account.name = acc.name || '';
      if (acc.connectionType !== undefined) {
        account.connectionType = acc.connectionType;
      }
      if (acc.roleName !== undefined) {
        account.roleName = acc.roleName;
      }
      if (acc.credentials !== undefined) {
        account.credentials = acc.credentials;
      }
      if (acc.externalId !== undefined) {
        account.externalId = acc.externalId;
      }
      return account;
    });
  }

  export async function createAccount(account: {
    accountId: string;
    name: string;
    connectionType?: string;
    roleName?: string;
    externalId?: string;
  }): Promise<void> {
    await apiService.post('/core/create-account', { body: account });
  }

  export async function updateAccount(account: {
    accountId: string;
    name?: string;
    connectionType?: string;
    roleName?: string;
    externalId?: string;
  }): Promise<void> {
    await apiService.post('/core/update-account', { body: account });
  }

  export async function deleteAccount(accountId: string): Promise<void> {
    await apiService.post('/core/delete-account', { body: { accountId } });
  }

  // ── Groups ─────────────────────────────────────────────

  export async function getGroups(): Promise<Group[]> {
    return apiService.get<Group[]>('/core/get-groups');
  }

  export async function createGroup(group: {
    name: string;
    description?: string;
  }): Promise<void> {
    await apiService.post('/core/create-group', { body: group });
  }

  export async function updateGroup(group: {
    groupId: string;
    name?: string;
    description?: string;
  }): Promise<void> {
    await apiService.post('/core/update-group', { body: group });
  }

  export async function deleteGroup(groupId: string): Promise<void> {
    await apiService.post('/core/delete-group', { body: { groupId } });
  }

  // ── Group Members ──────────────────────────────────────

  export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
    return apiService.post<GroupMember[]>('/core/get-group-members', {
      body: { groupId },
    });
  }

  export async function addGroupMember(groupId: string, userId: string): Promise<void> {
    await apiService.post('/core/add-group-member', {
      body: { groupId, userId },
    });
  }

  export async function removeGroupMember(groupId: string, userId: string): Promise<void> {
    await apiService.post('/core/remove-group-member', {
      body: { groupId, userId },
    });
  }

  // ── Group Accounts ─────────────────────────────────────

  export async function getGroupAccounts(groupId: string): Promise<GroupAccount[]> {
    const items = await apiService.post<any[]>('/core/get-group-accounts', {
      body: { groupId },
    });
    return items.map(item => ({
      groupId: item.groupId ?? groupId,
      accountId: item.accountId,
      accountName: item.name ?? item.accountName,
    }));
  }

  export async function assignGroupAccount(groupId: string, accountId: string): Promise<void> {
    await apiService.post('/core/assign-group-account', {
      body: { groupId, accountId },
    });
  }

  export async function unassignGroupAccount(groupId: string, accountId: string): Promise<void> {
    await apiService.post('/core/unassign-group-account', {
      body: { groupId, accountId },
    });
  }

  // ── Tenant Members ─────────────────────────────────────

  export async function getTenantMembers(): Promise<TenantMember[]> {
    return apiService.get<TenantMember[]>('/core/get-tenant-members');
  }

  export async function inviteTenantMember(member: {
    username: string;
    email: string;
    role?: TenantRole;
    givenName?: string;
    familyName?: string;
  }): Promise<void> {
    await apiService.post('/core/invite-tenant-member', { body: member });
  }

  export async function updateTenantMemberRole(
    userId: string,
    role: TenantRole
  ): Promise<void> {
    await apiService.post('/core/update-tenant-member-role', {
      body: { userId, role },
    });
  }

  export async function removeTenantMember(userId: string): Promise<void> {
    await apiService.post('/core/remove-tenant-member', { body: { userId } });
  }
}
